import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { getSystemSettings, updateSystemSettings } from '../services/settings';
import { SystemSettings as ImportedSystemSettings, DashboardSettings, Hotel, OptionSetting } from '../types/system';
import { SystemSettingsResponse } from '../types/systemSettings';

// システム設定管理ページ用の拡張 SystemSettings 型
// system.ts の定義に合わせるが、必須フィールドを設定する
interface SystemSettings {
  notification_enabled: boolean;
  language: string;
  map_default_zoom: number;
  gps_tracking_interval: number;
  line_notification_enabled: boolean;
  line_notifications_enabled: boolean;
  email_notifications_enabled: boolean;
  push_notifications_enabled: boolean;
  sms_notifications_enabled: boolean;
  notification_sound_enabled: boolean;
  store_name: string;
  store_address: string;
  phone_number: string;
  line_account: string;
  default_map_latitude: number;
  default_map_longitude: number;
  default_map_radius: number;
  theme: string;
  notify_new_orders: boolean;
  notify_shifts: boolean;
  notify_messages: boolean;
  maintenance_mode: boolean;
  system_email: string;
  backup_enabled: boolean;
  backup_frequency: string;
  backup_retention_days: number;
  log_level: string;
  max_upload_size: number;
  allowed_file_types: string[];
  business_hours_start: string;
  business_hours_end: string;
  courses: any[];
  hotels: any[];
  options: any[];
  business_hours: any[];
  allow_same_day_bookings: boolean;
  max_booking_days_ahead: number;
  min_booking_interval: number;
  cancellation_deadline_hours: number;
  cancellation_fee_rate: number;
  service_fee_rate: number;
  tax_rate: number;
  distance_fee_enabled: boolean;
  distance_fee_per_km: number;
  auto_backup_enabled: boolean;
  backup_interval_hours: number;
  last_backup_at: string | null;
  line_channel_id: string | null;
  line_channel_secret: string | null;
  line_channel_token: string | null;
  line_webhook_url: string | null;
  created_at: string;
  updated_at: string;
}

const SystemSettingsManagement: React.FC = () => {
  const [settings, setSettings] = useState<SystemSettings>({
    maintenance_mode: false,
    system_email: '',
    backup_enabled: false,
    backup_frequency: 'daily',
    backup_retention_days: 7,
    log_level: 'info',
    max_upload_size: 10,
    allowed_file_types: ['jpg', 'png', 'pdf'],
    notification_enabled: false,
    language: 'ja',
    map_default_zoom: 12,
    gps_tracking_interval: 60,
    line_notification_enabled: false,
    line_notifications_enabled: false,
    email_notifications_enabled: false,
    push_notifications_enabled: false,
    sms_notifications_enabled: false,
    notification_sound_enabled: false,
    store_name: '',
    phone_number: '',
    store_address: '',
    default_map_latitude: 35.6812,
    default_map_longitude: 139.7671,
    default_map_radius: 10,
    theme: 'light',
    business_hours_start: '18:00',
    business_hours_end: '05:00',
    line_account: '', // 必須フィールドに初期値設定
    notify_new_orders: false,
    notify_shifts: false,
    notify_messages: false,
    courses: [],
    hotels: [],
    options: [],
    business_hours: [],
    allow_same_day_bookings: true,
    max_booking_days_ahead: 30,
    min_booking_interval: 30,
    cancellation_deadline_hours: 24,
    cancellation_fee_rate: 100,
    service_fee_rate: 0,
    tax_rate: 10,
    distance_fee_enabled: false,
    distance_fee_per_km: 0,
    auto_backup_enabled: false,
    backup_interval_hours: 24,
    last_backup_at: null,
    line_channel_id: null,
    line_channel_secret: null,
    line_channel_token: null,
    line_webhook_url: null,
    created_at: '',
    updated_at: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await getSystemSettings();
      // APIから取得したデータを変換して不足フィールドに初期値を設定
      setSettings({
        ...data,
        line_account: data.line_account || '',
        maintenance_mode: data.maintenance_mode !== undefined ? data.maintenance_mode : false,
        system_email: data.system_email || '',
        backup_enabled: data.backup_enabled !== undefined ? data.backup_enabled : false,
        backup_frequency: data.backup_frequency || 'daily',
        backup_retention_days: data.backup_retention_days !== undefined ? data.backup_retention_days : 7,
        log_level: data.log_level || 'info',
        max_upload_size: data.max_upload_size !== undefined ? data.max_upload_size : 10,
        allowed_file_types: data.allowed_file_types || ['jpg', 'png', 'pdf'],
        theme: data.theme || 'light',
        business_hours_start: data.business_hours_start || '18:00',
        business_hours_end: data.business_hours_end || '05:00',
        notify_new_orders: data.notify_new_orders !== undefined ? data.notify_new_orders : false,
        notify_shifts: data.notify_shifts !== undefined ? data.notify_shifts : false,
        notify_messages: data.notify_messages !== undefined ? data.notify_messages : false,
        courses: data.courses || [],
        hotels: data.hotels || [],
        options: data.options || [],
        business_hours: data.business_hours || [],
        // 不足しているプロパティを追加
        line_notifications_enabled: data.line_notifications_enabled !== undefined ? data.line_notifications_enabled : false,
        email_notifications_enabled: data.email_notifications_enabled !== undefined ? data.email_notifications_enabled : false,
        push_notifications_enabled: data.push_notifications_enabled !== undefined ? data.push_notifications_enabled : false,
        sms_notifications_enabled: data.sms_notifications_enabled !== undefined ? data.sms_notifications_enabled : false,
        notification_sound_enabled: data.notification_sound_enabled !== undefined ? data.notification_sound_enabled : false,
        allow_same_day_bookings: data.allow_same_day_bookings !== undefined ? data.allow_same_day_bookings : true,
        max_booking_days_ahead: data.max_booking_days_ahead !== undefined ? data.max_booking_days_ahead : 30,
        min_booking_interval: data.min_booking_interval !== undefined ? data.min_booking_interval : 30,
        cancellation_deadline_hours: data.cancellation_deadline_hours !== undefined ? data.cancellation_deadline_hours : 24,
        cancellation_fee_rate: data.cancellation_fee_rate !== undefined ? data.cancellation_fee_rate : 100,
        service_fee_rate: data.service_fee_rate !== undefined ? data.service_fee_rate : 0,
        tax_rate: data.tax_rate !== undefined ? data.tax_rate : 10,
        distance_fee_enabled: data.distance_fee_enabled !== undefined ? data.distance_fee_enabled : false,
        distance_fee_per_km: data.distance_fee_per_km !== undefined ? data.distance_fee_per_km : 0,
        auto_backup_enabled: data.auto_backup_enabled !== undefined ? data.auto_backup_enabled : false,
        backup_interval_hours: data.backup_interval_hours !== undefined ? data.backup_interval_hours : 24,
        last_backup_at: data.last_backup_at || null,
        line_channel_id: data.line_channel_id || null,
        line_channel_secret: data.line_channel_secret || null,
        line_channel_token: data.line_channel_token || null,
        line_webhook_url: data.line_webhook_url || null,
        created_at: data.created_at || '',
        updated_at: data.updated_at || ''
      });
    } catch (err) {
      setError('システム設定の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              name === 'allowed_file_types' ? value.split(',').map(type => type.trim()) :
              value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      setLoading(true);
      // システム設定型をImportedSystemSettings型に変換して送信
      const systemSettingsData: ImportedSystemSettings = {
        ...settings
      } as ImportedSystemSettings;
      await updateSystemSettings(systemSettingsData);
      setSuccess('システム設定を更新しました');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'システム設定の更新に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !settings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          システム設定
        </Typography>
        <Paper sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.maintenance_mode}
                      onChange={handleChange}
                      name="maintenance_mode"
                    />
                  }
                  label="メンテナンスモード"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="システムメールアドレス"
                  name="system_email"
                  type="email"
                  value={settings.system_email}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.backup_enabled}
                      onChange={handleChange}
                      name="backup_enabled"
                    />
                  }
                  label="自動バックアップ"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="バックアップ頻度"
                  name="backup_frequency"
                  value={settings.backup_frequency}
                  onChange={handleChange}
                  select
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="hourly">毎時</option>
                  <option value="daily">毎日</option>
                  <option value="weekly">毎週</option>
                  <option value="monthly">毎月</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="バックアップ保持期間（日）"
                  name="backup_retention_days"
                  type="number"
                  value={settings.backup_retention_days}
                  onChange={handleChange}
                  inputProps={{ min: 1, max: 365 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ログレベル"
                  name="log_level"
                  value={settings.log_level}
                  onChange={handleChange}
                  select
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="debug">Debug</option>
                  <option value="info">Info</option>
                  <option value="warn">Warning</option>
                  <option value="error">Error</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="最大アップロードサイズ（MB）"
                  name="max_upload_size"
                  type="number"
                  value={settings.max_upload_size}
                  onChange={handleChange}
                  inputProps={{ min: 1, max: 100 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="許可されるファイル形式（カンマ区切り）"
                  name="allowed_file_types"
                  value={settings.allowed_file_types.join(',')}
                  onChange={handleChange}
                  helperText="例: jpg,png,pdf,doc,docx"
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {loading ? '更新中...' : '設定を保存'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default SystemSettingsManagement; 